import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    ListItemText,
    Typography
 } from "@material-ui/core";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { v4 as uuidv4 } from "uuid";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { Form, Formik } from "formik";
import ShowTicketOpen from "../ShowTicketOpenModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const useStyles = makeStyles((theme) => ({
	autoComplete: { 
		width: "100%",
	},
	maxWidth: {
		width: "100%",
	},
	buttonColorError: {
		color: theme.palette.error.main,
		borderColor: theme.palette.error.main,
	},
	connectionStatus: {
		fontSize: 11,
	},
	connectionOnline: {
		color: "#25d366",
	},
	connectionOffline: {
		color: "#e1306c",
	},
}));

const AcceptTicketWithouSelectQueue = ({ modalOpen, onClose, ticketId, ticket }) => {
	const history = useHistory();
	const classes = useStyles();
	const [selectedQueue, setSelectedQueue] = useState('');
	const [selectedWhatsapp, setSelectedWhatsapp] = useState('');
	const [whatsapps, setWhatsapps] = useState([]);
	const [loading, setLoading] = useState(false);
	const { user } = useContext(AuthContext);
	const [ openAlert, setOpenAlert ] = useState(false);
	const [ userTicketOpen, setUserTicketOpen] = useState("");
	const [ queueTicketOpen, setQueueTicketOpen] = useState("");
	const { tabOpen, setTabOpen } = useContext(TicketsContext);

	const {get:getSetting} = useCompanySettings();

useEffect(() => {
	const { companyId } = user;

	// Buscar lista de conexões disponíveis
	api.get(`/whatsapp`, { params: { companyId, session: 0 } })
		.then(({ data }) => setWhatsapps(data))
		.catch((err) => toastError(err));

	// Pré-selecionar a conexão que recebeu o ticket
	if (ticket?.whatsappId) {
		setSelectedWhatsapp(ticket.whatsappId);
	}

	// Auto-selecionar fila se houver apenas uma
	if (user.queues.length === 1) {
		setSelectedQueue(user.queues[0].id);
	}
}, []);

const IconChannel = (channel) => {
	switch (channel) {
		case "facebook":
			return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
		case "instagram":
			return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
		case "whatsapp":
			return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
		default:
			return null;
	}
};

const handleClose = () => {
	onClose();
	setSelectedQueue("");
	setSelectedWhatsapp("");
};

const handleCloseAlert = () => {
	setOpenAlert(false);
	setLoading(false)
};

const handleSendMessage = async (id) => {

	let isGreetingMessage = false;

	try {
		const  setting  = await getSetting({
			"column":"sendGreetingAccepted"
		});
		if (setting.sendGreetingAccepted === "enabled") isGreetingMessage = true;
	} catch (err) {
		toastError(err);
	}
	
	let settingMessage
	try {
		settingMessage = await getSetting({
			"column": "greetingAcceptedMessage"
		})
	} catch (err) {
		toastError(err);
	}
	
	if (isGreetingMessage && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled") && ticket.status === "pending") {
		const msg = `${settingMessage.greetingAcceptedMessage}`;
		const message = {
			read: 1,
			fromMe: true,
			mediaUrl: "",
			body: `${msg.trim()}`,
		};
		try {
			await api.post(`/messages/${id}`, message);
		} catch (err) {
			toastError(err);
		}
	}
};

const handleUpdateTicketStatus = async (queueId) => {
	setLoading(true);
	try {
		const isSameConnection = String(selectedWhatsapp) === String(ticket.whatsappId);

		if (isSameConnection) {
			// Fluxo original — mesma conexão
			const otherTicket = await api.put(`/tickets/${ticketId}`, {
				status: ticket.isGroup && ticket.channel === 'whatsapp' ? "group" : "open",
				userId: user?.id || null,
				queueId: queueId
			});

			if (otherTicket.data.id !== ticket.id) {
				if (otherTicket.data.userId !== user?.id) {
					setOpenAlert(true)
					setUserTicketOpen(otherTicket.data.user.name)
					setQueueTicketOpen(otherTicket.data.queue.name)
				} else {
					setLoading(false);
					setTabOpen(otherTicket.isGroup ? "group" : "open");
					history.push(`/tickets/${otherTicket.data.uuid}`);
				}
			} else {
				handleSendMessage(ticket.id)
				setLoading(false);
				setTabOpen(ticket.isGroup ? "group" : "open");
				history.push(`/tickets/${ticket.uuid}`);
				handleClose();
			}
		} else {
			// Conexão diferente — criar novo ticket e fechar o anterior
			try {
				// 1. Tentar criar novo ticket na nova conexão
				const { data: newTicket } = await api.post("/tickets", {
					contactId: ticket.contactId,
					queueId: queueId,
					whatsappId: selectedWhatsapp,
					userId: user?.id,
					status: "open",
				});

				// 2. Fechar o ticket anterior sem mensagem de despedida
				await api.put(`/tickets/${ticketId}`, {
					status: "closed",
					userId: user?.id || null,
					sendFarewellMessage: false,
					amountUsedBotQueues: 0
				});

				// 3. Navegar para o novo ticket
				setLoading(false);
				setTabOpen(newTicket.isGroup ? "group" : "open");
				history.push(`/tickets/${newTicket.uuid}`);
				handleClose();

			} catch (createErr) {
				// Ticket já existe aberto nessa conexão — backend retorna o ticket existente como JSON no erro
				try {
					const existingTicket = JSON.parse(createErr?.response?.data?.error);

					if (existingTicket && existingTicket.uuid) {
						// Fechar o ticket original sem mensagem de despedida
						await api.put(`/tickets/${ticketId}`, {
							status: "closed",
							userId: user?.id || null,
							sendFarewellMessage: false,
							amountUsedBotQueues: 0
						});

						// Navegar diretamente para o ticket já aberto nessa conexão
						setLoading(false);
						setTabOpen(existingTicket.isGroup ? "group" : "open");
						history.push(`/tickets/${existingTicket.uuid}`);
						handleClose();
					} else {
						setLoading(false);
						toastError(createErr);
					}
				} catch (_parseErr) {
					setLoading(false);
					toastError(createErr);
				}
			}
		}
	} catch (err) {
		setLoading(false);
		toastError(err);
	}
};

return (
	<>
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="xs" fullWidth>
			<DialogTitle id="form-dialog-title">
				{i18n.t("ticketsList.acceptModal.title")}
			</DialogTitle>
			<DialogContent dividers>
				{/* Seleção de Fila */}
				<FormControl variant="outlined" className={classes.maxWidth}>
					<InputLabel>{i18n.t("ticketsList.acceptModal.queue")}</InputLabel>
					<Select
						value={selectedQueue}
						className={classes.autoComplete}
						onChange={(e) => setSelectedQueue(e.target.value)}
						label={i18n.t("ticketsList.acceptModal.queue")}
					>
						<MenuItem value={''}>&nbsp;</MenuItem>
						{user.queues.map((queue) => (
							<MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Seleção de Conexão */}
				<FormControl variant="outlined" className={classes.maxWidth} style={{ marginTop: 16 }}>
					<InputLabel>Conexão</InputLabel>
					<Select
						value={selectedWhatsapp}
						className={classes.autoComplete}
						onChange={(e) => setSelectedWhatsapp(e.target.value)}
						label="Conexão"
					>
						{whatsapps?.length > 0 && whatsapps.map((whatsapp, key) => (
							<MenuItem dense key={key} value={whatsapp.id}>
								<ListItemText
									primary={
										<>
											{IconChannel(whatsapp.channel)}
											<Typography
												component="span"
												style={{
													fontSize: 14,
													marginLeft: 10,
													display: "inline-flex",
													alignItems: "center",
													lineHeight: "2"
												}}
											>
												{whatsapp.name}&nbsp;
												<span
													className={classes.connectionStatus}
													style={{
														color: whatsapp.status === 'CONNECTED'
															? "#25d366"
															: "#e1306c"
													}}
												>
													({whatsapp.status})
												</span>
											</Typography>
										</>
									}
								/>
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={handleClose}
					className={classes.buttonColorError}
					disabled={loading}
					variant="outlined"
				>
					{i18n.t("ticketsList.buttons.cancel")}
				</Button>
				<ButtonWithSpinner
					variant="contained"
					type="button"
					disabled={selectedQueue === "" || selectedWhatsapp === ""}
					onClick={() => handleUpdateTicketStatus(selectedQueue)}
					color="primary"
					loading={loading}
				>
					{i18n.t("ticketsList.buttons.start")}
				</ButtonWithSpinner>
			</DialogActions>
			<ShowTicketOpen
				isOpen={openAlert}
				handleClose={handleCloseAlert}
				user={userTicketOpen}
				queue={queueTicketOpen}
			/>
		</Dialog>
	</>
);
};

export default AcceptTicketWithouSelectQueue;